import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { getLegacyLegalTransitions } from "@nexus/order-status";

import type {
  OrderLineVideo,
  OrderScriptRow,
  PartnerPreflightIntent,
} from "@/features/marketplace/types/marketplace.types";
import { orderLineVideoFulfillmentStatus } from "@/features/marketplace/lib/video-display-status";
import { MAX_VIDEOS_PER_LINE } from "@/features/marketplace/lib/video-slot-limits";

import { isPartnerPreflightNotifyRetry } from "./partner-preflight-retry";

export type { PartnerPreflightIntent };
export { MAX_VIDEOS_PER_LINE };

const CLOSED_TO_VIDEO_CHANGES = new Set<MarketplaceFulfillmentStatus>([
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "CANCELLED",
  "COMPLETED_PRE_ADGE",
]);

const TERMINAL_LINE_FOR_PARTNER_VIDEOS = new Set<MarketplaceFulfillmentStatus>([
  "ORDER_DELIVERED",
  "CANCELLED",
  "COMPLETED_PRE_ADGE",
]);

/**
 * Mirrors the API's `lineAllowsPartnerEmptySlotAllocation`: a script delivers
 * several videos over time, so a new slot stays allowed while earlier videos
 * sit in final approval.
 */
const LINE_STATUSES_OPEN_TO_SLOT_ALLOCATION = new Set<MarketplaceFulfillmentStatus>([
  "SCRIPT",
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
]);

type PartnerVideoScriptRef = {
  fulfillmentStatus: OrderScriptRow["fulfillmentStatus"];
  videos?: readonly OrderLineVideo[] | OrderLineVideo[] | null;
};

export function scriptHasVideoNeedingPartnerRevision(
  script: PartnerVideoScriptRef,
): boolean {
  if (script.fulfillmentStatus === "SENT_FOR_REVISION") return true;
  const videos = partnerLineVideos(script as OrderScriptRow);
  return videos.some((video) => {
    if (video.reviewStatus === "REVISION_REQUESTED") return true;
    return (
      orderLineVideoFulfillmentStatus(script.fulfillmentStatus, video, {
        videos: script.videos ?? undefined,
      }) === "SENT_FOR_REVISION"
    );
  });
}

/** Line-level gate for partner upload, replace, and empty-slot fill. */
export function canPartnerManageScriptVideos(script: PartnerVideoScriptRef): boolean {
  if (TERMINAL_LINE_FOR_PARTNER_VIDEOS.has(script.fulfillmentStatus)) {
    return false;
  }
  if (scriptHasVideoNeedingPartnerRevision(script)) return true;
  if (canChangePartnerVideos(script.fulfillmentStatus)) return true;
  return partnerEmptyVideoSlots(script as OrderScriptRow).length > 0;
}

export function canPartnerManageVideoSlot(
  script: PartnerVideoScriptRef,
  video: OrderLineVideo,
): boolean {
  if (TERMINAL_LINE_FOR_PARTNER_VIDEOS.has(script.fulfillmentStatus)) {
    return false;
  }
  // Empty slots and videos sent back for revision stay uploadable while the
  // line is in production, including when another video is in final approval.
  if (partnerVideoSlotEmpty(video)) return true;
  if (video.reviewStatus === "REVISION_REQUESTED") return true;
  const status =
    video.fulfillmentStatus ??
    orderLineVideoFulfillmentStatus(script.fulfillmentStatus, video, {
      videos: script.videos ?? undefined,
    });
  if (status === "SENT_FOR_REVISION") return true;
  return canChangePartnerVideos(script.fulfillmentStatus);
}

export function partnerLineVideos(script: OrderScriptRow): OrderLineVideo[] {
  return [...(script.videos ?? [])].sort((left, right) => left.position - right.position);
}

export function partnerVideoSlotEmpty(video: OrderLineVideo): boolean {
  return (
    video.isEmpty === true ||
    (video.preflightUploadId === null &&
      video.scriptReviewId === null &&
      !video.preflightUrl?.trim())
  );
}

export function partnerEmptyVideoSlots(script: OrderScriptRow): OrderLineVideo[] {
  return partnerLineVideos(script).filter(partnerVideoSlotEmpty);
}

export function partnerFilledVideoSlots(script: OrderScriptRow): OrderLineVideo[] {
  return partnerLineVideos(script).filter((video) => !partnerVideoSlotEmpty(video));
}

export function partnerMustPickVideoSlot(script: OrderScriptRow): boolean {
  const videos = partnerLineVideos(script);
  if (videos.length > 1) return true;
  return partnerFilledVideoSlots(script).length > 0;
}

export function partnerVideoSelectionKey(video: OrderLineVideo): string {
  return video.id ?? `legacy-${video.position}`;
}

export function canChangePartnerVideos(
  status: MarketplaceFulfillmentStatus,
): boolean {
  return !CLOSED_TO_VIDEO_CHANGES.has(status);
}

/** Whether the partner can allocate another empty video slot on this line. */
export function canPartnerAddVideoSlot(script: OrderScriptRow): boolean {
  if (TERMINAL_LINE_FOR_PARTNER_VIDEOS.has(script.fulfillmentStatus)) return false;
  if (partnerLineVideos(script).length >= MAX_VIDEOS_PER_LINE) return false;
  if (LINE_STATUSES_OPEN_TO_SLOT_ALLOCATION.has(script.fulfillmentStatus)) {
    return true;
  }
  return canPartnerManageScriptVideos(script);
}

/** Whether the partner can remove an unused empty slot (line must keep at least one). */
export function canPartnerDeleteEmptyVideoSlot(
  script: OrderScriptRow,
  video: OrderLineVideo,
): boolean {
  if (!video.id) return false;
  if (!partnerVideoSlotEmpty(video)) return false;
  if (!canPartnerManageScriptVideos(script)) return false;
  return partnerLineVideos(script).length > 1;
}

/** Whether the partner can fill an empty slot on this line. */
export function canAddPartnerVideo(script: OrderScriptRow): boolean {
  if (!canPartnerManageScriptVideos(script)) return false;
  const allocated = partnerLineVideos(script).length;
  if (allocated >= MAX_VIDEOS_PER_LINE && partnerEmptyVideoSlots(script).length === 0) {
    return false;
  }
  return partnerEmptyVideoSlots(script).length > 0;
}

/** Whether the partner workspace should show the Upload tab (and upload entry points). */
export function partnerWorkspaceShowsUploadTab(script: OrderScriptRow): boolean {
  if (isPartnerPreflightNotifyRetry(script)) {
    return true;
  }
  if (script.notifyState === "PENDING_NOTIFY") {
    return false;
  }
  const canSubmitForFeedback = getLegacyLegalTransitions(
    script.fulfillmentStatus,
    "vendor",
  ).includes("SUBMITTED_FOR_FEEDBACK");
  const hasVideos = partnerLineVideos(script).length > 0;
  const canManageVideos = hasVideos && canPartnerManageScriptVideos(script);
  const canFillEmptySlot = canAddPartnerVideo(script);
  const canAllocateSlot = hasVideos && canPartnerAddVideoSlot(script);
  return (
    canSubmitForFeedback ||
    canManageVideos ||
    canFillEmptySlot ||
    canAllocateSlot
  );
}

/**
 * When the partner has not picked a row explicitly, infer replace vs empty-slot
 * fill so a lone filled video does not hit the legacy submit path.
 */
export function partnerDefaultReplaceVideo(script: OrderScriptRow): OrderLineVideo | null {
  const videos = partnerLineVideos(script);
  if (videos.length === 0) return null;
  const needsRevision = videos.find((video) => {
    if (video.reviewStatus === "REVISION_REQUESTED") return true;
    return (
      orderLineVideoFulfillmentStatus(script.fulfillmentStatus, video, {
        videos: script.videos,
      }) === "SENT_FOR_REVISION"
    );
  });
  if (needsRevision) return needsRevision;
  const canAdd = canAddPartnerVideo(script);
  const filled = partnerFilledVideoSlots(script);
  if (!canAdd && filled.length >= 1) {
    return filled[0] ?? null;
  }
  if (videos.length === 1 && !partnerVideoSlotEmpty(videos[0])) {
    return videos[0];
  }
  return null;
}

/**
 * First upload on a line with no slots: today's submit (client emails, video 1).
 * Filling or replacing a slot: deliverableId on the stored row.
 */
export function resolvePartnerPreflightIntent(
  script: OrderScriptRow,
  selectedVideo: OrderLineVideo | null,
): PartnerPreflightIntent | undefined {
  const videos = partnerLineVideos(script);
  if (videos.length === 0) return undefined;

  const target = selectedVideo ?? partnerDefaultReplaceVideo(script);
  if (target) {
    return target.id ? { deliverableId: target.id } : undefined;
  }

  if (partnerMustPickVideoSlot(script)) {
    return undefined;
  }

  const soleEmpty = partnerEmptyVideoSlots(script);
  if (soleEmpty.length === 1 && soleEmpty[0]?.id) {
    return { deliverableId: soleEmpty[0].id };
  }

  return undefined;
}

export function reviewIdAfterPartnerSubmit(
  script: OrderScriptRow | undefined,
  intent: PartnerPreflightIntent | undefined,
): string | null {
  if (!script) return null;
  if (intent && "deliverableId" in intent) {
    return (
      script.videos?.find((video) => video.id === intent.deliverableId)
        ?.scriptReviewId ?? script.scriptReviewId
    );
  }
  return script.scriptReviewId;
}
